import { Button } from "@components/ui/button";
import { InputGroup, InputGroupInput } from "@components/ui/input-group";
import { Lock, LockOpen, Trash2 } from "lucide-react";
import { forwardRef } from "react";

interface InputLockableProps {
  placeholder: string;
  value?: string;
  isLocked?: boolean;
  isInvalid?: boolean;
  isShimmering?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onLockToggle?: () => void;
  onDelete?: () => void;
  name?: string;
}

const InputLockable = forwardRef<HTMLInputElement, InputLockableProps>(
  (
    {
      placeholder,
      value,
      isLocked = false,
      isInvalid = false,
      isShimmering = false,
      onChange,
      onBlur,
      onLockToggle,
      onDelete,
      name,
    },
    ref
  ) => {
    const effectivelyLocked = isLocked || isShimmering;

    return (
      <div className={`flex items-center gap-1 transition-opacity ${isShimmering ? "opacity-60" : ""}`}>
        {onLockToggle ? (
          <Button
            type="button"
            onClick={onLockToggle}
            variant="ghost"
            size="icon"
            disabled={isShimmering}
            data-testid="lock-toggle-button"
            data-locked={isLocked}
          >
            {effectivelyLocked ? (
              <Lock size={12} className="text-base" />
            ) : (
              <LockOpen size={12} className="text-[#96989B]" />
            )}
          </Button>
        ) : null}
        {onDelete ? (
          <Button
            type="button"
            onClick={onDelete}
            variant="ghost"
            size="icon"
            disabled={isShimmering}
            data-testid="delete-button"
          >
            <Trash2 size={14} className="text-[#96989B]" />
          </Button>
        ) : null}
        <InputGroup
          className={`h-10 relative overflow-hidden ${effectivelyLocked ? "border-[#2E3238]" : ""}`}
          data-testid="lockable-input-group"
        >
          <InputGroupInput
            ref={ref}
            name={name}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            disabled={isShimmering}
            className={`disabled:opacity-100 ${isShimmering ? "text-transparent" : ""}`}
            aria-invalid={isInvalid}
            data-testid="lockable-input"
          />
          {isShimmering && value && (
            <span
              className="text-shimmer-overlay absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-sm text-neutral-400 whitespace-nowrap overflow-hidden max-w-[calc(100%-1.5rem)]"
            >
              {value}
            </span>
          )}
        </InputGroup>
      </div>
    );
  }
);

InputLockable.displayName = "InputLockable";

export default InputLockable;
